import React, { useState, useEffect } from "react";

// Import ESRI Components
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";

// Load components
import DrawHandler from "./DrawHandler";

// Import Semantic UI React
import { Button, Dropdown, Icon, Popup } from "semantic-ui-react";

type GraphicsLayerPrint = GraphicsLayer & { print: boolean };

var layer = null;

export default function IdentifyWidget({
  active,
  view,
  setIdentifyGeometry,
  setTooltipMessage,
  setTooltipVisible,
  selectedParcel,
  setBufferModalOpen,
}) {
  const [activeIdentifyTool, setactiveIdentifyTool] = useState("point");
  const [handler, setHandler] = useState(null);
  const [drawHandler, setDrawHandler] = useState(null);
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);

  // console.log("IdentifyWidget", { active, activeTool, activeIdentifyTool });

  useEffect(() => {
    switch (activeIdentifyTool) {
      case "rectangle":
        setTooltipMessage(
          "Click once to start drawing the rectangle, click again to complete the feature"
        );
        setTooltipVisible(true);
        break;
      case "point":
        setTooltipMessage(null);
        setTooltipVisible(false);
        break;
      default:
        setTooltipMessage(
          "Click to start drawing, click for each vertex, then double clicking will complete the feature"
        );
        setTooltipVisible(true);
        break;
    }
    setOpen(false);
    setHover(false);
  }, [activeIdentifyTool]);

  // called on first load
  useEffect(() => {
    layer = new GraphicsLayer({
      id: "IdentifyTaskLayer",
      print: true,
    } as GraphicsLayerPrint);

    // Add the layer to the view
    view.map.layers.add(layer);

    // Widgets for the drawing tool
    setDrawHandler(
      new DrawHandler({
        view: view,
        layer: layer,
        clear_graphic: true,
      })
    );
  }, [view]);

  useEffect(() => {
    if (drawHandler) {
      // Listen for the draw handler to be completed
      drawHandler.on("completed", (geometry) => {
        // execute the identify task with the geometry
        setIdentifyGeometry(geometry);

        // Set the active tool back to default
        setTimeout(() => {
          clearGraphics();
          // Set the default identify
          setactiveIdentifyTool("point");
        }, 500);
      });
    }
  }, [drawHandler, setIdentifyGeometry]);

  useEffect(() => {
    clearDrawHandler();
    clearHandler();
    clearGraphics();
    // if the measurement tool is active then hook up handler
    if (active) {
      if (["polyline", "rectangle", "polygon"].includes(activeIdentifyTool)) {
        drawHandler.start(activeIdentifyTool);
        view.focus();
      } else if (activeIdentifyTool === "point") {
        defaultIdentify();
      }
    }
  }, [active, activeIdentifyTool]); // eslint-disable-line react-hooks/exhaustive-deps

  // const checkActiveState = () => {
  //   if
  // }

  function clearDrawHandler() {
    if (drawHandler && drawHandler !== null) {
      drawHandler.end();
    }
  }

  function defaultIdentify() {
    // listen for a click event on the map
    clearHandler();
    setHandler(
      view.on("click", (event) => {
        if (event.button === 0) {
          setIdentifyGeometry(event.mapPoint);
        }
      })
    );
  }

  function clearGraphics() {
    if (layer) {
      // remove all the graphics
      layer.graphics.removeAll();
    }
  }

  function clearHandler() {
    if (handler && handler !== null) {
      handler.remove();
      //   handler = null;
      setHandler(null);
    }
  }

  // if the tool isn't active then don't render anything
  if (active === false) {
    return null;
  }

  return (
    <Popup
      trigger={
        <Dropdown
          icon={{ className: `${icons[activeIdentifyTool].icon}` }}
          pointing="left"
          className="ui icon button no-margin no-focus"
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <Dropdown.Menu>
            <Dropdown.Header content="Identify By" />
            <Dropdown.Divider />
            {renderList()}
            <Dropdown.Divider />
            <Dropdown.Item
              disabled={!selectedParcel}
              onClick={() => setBufferModalOpen(true)}
            >
              <Icon className="esri-icon-lasso icon" />
              Buffer Parcel
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      }
      className={`transition fade ${hover && !open ? "in" : "out"}`}
      on={"hover"}
      open={hover && !open}
      inverted
      size="tiny"
      content="Identify"
      position="right center"
    />
  );

  function renderList() {
    return ["point", "rectangle", "polygon", "polyline"].map((tool, index) => {
      if (activeIdentifyTool !== tool) {
        return (
          <Dropdown.Item
            key={index}
            onClick={() => setactiveIdentifyTool(tool)}
          >
            <Icon className={`${icons[tool].icon}`} />
            {icons[tool].label}
          </Dropdown.Item>
        );
      } else {
        return null;
      }
    });
  }
}

const icons = {
  point: {
    label: "Point",
    icon: "info circle icon",
  },
  rectangle: {
    label: "Extent",
    icon: "expand icon",
  },
  polygon: {
    label: "Polygon",
    icon: "esri-icon-polygon icon",
  },
  polyline: {
    label: "Polyline",
    icon: "esri-icon-polyline icon",
  },
};
